import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../i18n/index.js';
import { viewportHeight } from '../platform/pointer.js';

/**
 * 0.157 (ADR 0163, Briefing „Flüssige Oberfläche“ F02): Ladestrukturen statt „Lädt …“. Sie zeigen die Geometrie der
 * Zielansicht (Kopf, Zeilen, Karten), sind nicht fokussierbar und für Screenreader verborgen; der umgebende Bereich ist
 * `aria-busy`. Erst nach 800 ms folgt ein kurzer Status für Screenreader – kein Blinken bei schnellen Antworten, keine
 * Welle über die Seite (ruhige Flächen; Bewegung nur ohne „Reduziert“).
 */
const bar = 'rounded-md bg-zinc-200/70 dark:bg-zinc-800/70';

export function SkeletonBar({ className = '' }: { className?: string }) {
  return <span aria-hidden className={`block ${bar} ${className}`} />;
}

/** Bereich, der lädt: aria-busy, Geometrie als Kinder, verzögerter Status für Screenreader */
export function Loading({ children, label, className = '' }: { children: ReactNode; label?: string; className?: string }) {
  const [long, setLong] = useState(false);
  useEffect(() => {
    const timer = window.setTimeout(() => setLong(true), 800);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <div aria-busy="true" data-skeleton="" className={`skeleton ${className}`}>
      <div aria-hidden className="pointer-events-none select-none">
        {children}
      </div>
      <span role="status" className="sr-only">
        {long ? (label ?? t('load.long')) : ''}
      </span>
    </div>
  );
}

/** Zeilen einer Liste – Anzahl aus der sichtbaren Fläche, nicht als Behauptung über Treffer */
export function rowsFor(rowPx: number, offsetPx = 260, max = 14) {
  return Math.max(3, Math.min(max, Math.floor((viewportHeight() - offsetPx) / rowPx)));
}

export function ListSkeleton({ rows, compact, withInput = true }: { rows?: number; compact?: boolean; withInput?: boolean }) {
  const n = rows ?? rowsFor(compact ? 36 : 48);
  return (
    <div className="space-y-1">
      {withInput && <SkeletonBar className="mb-3 h-11 w-full" />}
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className={`flex items-center gap-3 ${compact ? 'h-9' : 'h-11'}`}>
          <SkeletonBar className="size-5 shrink-0 rounded-full" />
          <SkeletonBar className={`h-3 ${['w-2/3', 'w-1/2', 'w-3/4', 'w-2/5'][i % 4]}`} />
        </div>
      ))}
    </div>
  );
}

export function CardsSkeleton({ cards = 2, lines = 3 }: { cards?: number; lines?: number }) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {Array.from({ length: cards }, (_, i) => (
        <div key={i} className="space-y-2 rounded-2xl border border-zinc-200 p-3 dark:border-zinc-800">
          <SkeletonBar className="h-4 w-1/3" />
          {Array.from({ length: lines }, (_, j) => (
            <SkeletonBar key={j} className={`h-3 ${['w-5/6', 'w-2/3', 'w-3/4'][j % 3]}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Werkzeugkopf (wie `ToolHeader`/`.section-toolbar`) */
export function HeaderSkeleton() {
  return (
    <div className="section-toolbar">
      <SkeletonBar className="h-6 w-40" />
      <span className="flex-1" />
      <SkeletonBar className="h-9 w-28" />
    </div>
  );
}

export type SkeletonKind = 'list' | 'board' | 'table' | 'calendar' | 'grid' | 'cards' | 'split' | 'overview';

/** Ladestruktur je Ansicht – nur Maße und Struktur, nie dasselbe große Paket wie die Ansicht */
export function ViewSkeleton({ kind, label }: { kind: SkeletonKind; label?: string }) {
  return (
    <Loading label={label}>
      {kind === 'overview' ? (
        <div className="space-y-4">
          <SkeletonBar className="h-5 w-1/2" />
          <CardsSkeleton cards={2} />
          <div className="space-y-3 rounded-2xl border border-zinc-200 p-4 dark:border-zinc-800">
            <SkeletonBar className="h-4 w-32" />
            <SkeletonBar className="h-8 w-24" />
            <SkeletonBar className="h-2 w-full" />
          </div>
        </div>
      ) : kind === 'board' ? (
        <div className="flex gap-3 overflow-hidden">
          {[0, 1, 2].map((c) => (
            <div key={c} className="w-72 shrink-0 space-y-2 rounded-xl bg-zinc-100/70 p-2 dark:bg-zinc-900/60">
              <SkeletonBar className="h-5 w-24" />
              {[0, 1, 2].map((r) => (
                <SkeletonBar key={r} className="h-16 w-full" />
              ))}
            </div>
          ))}
        </div>
      ) : kind === 'table' ? (
        <div className="space-y-1">
          <SkeletonBar className="h-9 w-full" />
          {Array.from({ length: rowsFor(40) }, (_, i) => (
            <SkeletonBar key={i} className="h-8 w-full opacity-60" />
          ))}
        </div>
      ) : kind === 'calendar' ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }, (_, i) => (
            <SkeletonBar key={i} className="h-20" />
          ))}
        </div>
      ) : kind === 'grid' ? (
        <>
          <HeaderSkeleton />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <SkeletonBar key={i} className="aspect-[4/3] w-full" />
            ))}
          </div>
        </>
      ) : kind === 'cards' ? (
        <>
          <HeaderSkeleton />
          <CardsSkeleton cards={4} />
        </>
      ) : kind === 'split' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[16rem_minmax(0,1fr)]">
          <div className="space-y-2">
            <SkeletonBar className="h-10 w-full" />
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonBar key={i} className={`h-4 ${['w-3/4', 'w-2/3', 'w-1/2'][i % 3]}`} />
            ))}
          </div>
          <div className="space-y-3">
            <SkeletonBar className="h-7 w-1/2" />
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonBar key={i} className={`h-3 ${['w-full', 'w-5/6', 'w-4/5'][i % 3]}`} />
            ))}
          </div>
        </div>
      ) : (
        <>
          <HeaderSkeleton />
          <ListSkeleton withInput={false} />
        </>
      )}
    </Loading>
  );
}

/** Erster Abruf fehlgeschlagen: verständlich, mit Wiederholung, im vorgesehenen Bereich */
export function LoadError({ onRetry, text }: { onRetry: () => void; text?: string }) {
  return (
    <div role="alert" data-load-error="" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-100">
      <span className="min-w-0 flex-1">{text ?? t('load.failed')}</span>
      <button type="button" onClick={onRetry} className="min-h-11 rounded-lg px-3 font-medium underline-offset-2 hover:underline" data-load-retry="">
        {t('load.retry')}
      </button>
    </div>
  );
}

/** Aktualisierung fehlgeschlagen, alter Stand bleibt sichtbar */
export function StaleNotice({ onRetry }: { onRetry: () => void }) {
  return (
    <p role="status" data-stale="" className="flex flex-wrap items-center gap-2 text-xs text-amber-800 dark:text-amber-300">
      {t('load.stale')}
      <button type="button" onClick={onRetry} className="min-h-9 rounded px-2 font-medium underline">
        {t('load.retry')}
      </button>
    </p>
  );
}
